import { Component, inject, OnInit, signal } from '@angular/core';
import { QuillModule } from 'ngx-quill';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { FormsModule, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatMenuModule } from '@angular/material/menu';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { provideNativeDateAdapter } from '@angular/material/core';
import { GRADES, CAREERS, INTERESTS } from '../profile-setup-dialog/profile-setup.constants.js';
import { PostService } from '../../../services/posts.service.js';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DraftEvent, Post } from '../../../models/post.js';
import { GroupService } from '../../../services/group.service.js';
import { Group } from '../../../models/group.js';
import { ApiEvent, EventService } from '../../../services/event.service';
import { ConfirmCancelDialog } from '../confirm-cancel-dialog/confirm-cancel-dialog';
import { eventWhenLabel } from '../../../helpers/event-helpers';

function timeToDisplay(time: string): string {
  const [h, m] = time.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${m.toString().padStart(2, '0')} ${period}`;
}

function displayToTime(display: string): string {
  const trimmed = display.trim();
  const match = trimmed.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/i);
  if (!match) return '';
  let hours = parseInt(match[1]);
  const minutes = match[2] ?? '00';
  const period = match[3].toUpperCase();
  if (hours < 1 || hours > 12 || parseInt(minutes) > 59) return '';
  if (period === 'PM' && hours !== 12) hours += 12;
  if (period === 'AM' && hours === 12) hours = 0;
  return `${hours.toString().padStart(2, '0')}:${minutes}:00`;
}

function buildTimeOptions(): string[] {
  const options: string[] = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      options.push(
        timeToDisplay(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:00`),
      );
    }
  }
  return options;
}

const TIME_OPTIONS = buildTimeOptions();

function formatDate(date: Date): string {
  const y = date.getFullYear();
  const mo = (date.getMonth() + 1).toString().padStart(2, '0');
  const d = date.getDate().toString().padStart(2, '0');
  return `${y}-${mo}-${d}`;
}

@Component({
  selector: 'app-post-dialog',
  imports: [
    QuillModule,
    MatDialogModule,
    FormsModule,
    MatButtonModule,
    MatInputModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatMenuModule,
    MatSlideToggleModule,
    MatDatepickerModule,
    MatAutocompleteModule,
  ],
  providers: [provideNativeDateAdapter()],
  templateUrl: './post-dialog.html',
  styleUrl: './post-dialog.css',
})
export class PostDialog implements OnInit {
  /** Reference to this dialog instance, used to close it on submit or cancel. */
  dialogRef = inject(MatDialogRef<PostDialog>);

  /** Service for post creation and image upload. */
  postService = inject(PostService);

  groupService = inject(GroupService);

  /** Service used to create the optional linked event. */
  eventService = inject(EventService);

  /** Opens the confirmation shown before linked events are shared with the post's audience. */
  dialog = inject(MatDialog);

  /** Shows a dismissible popup for submit errors (e.g. a rejected post). */
  snackBar = inject(MatSnackBar);

  /** Controls visibility of the audience filter selects in the template. */
  filter = false;

  /** Available career options sourced from profile-setup constants. */
  careers = CAREERS;

  /** Available interest options sourced from profile-setup constants. */
  interests = INTERESTS;

  /** Available grade options sourced from profile-setup constants. */
  grades = GRADES;

  /** 15-minute time options for the linked event's time autocompletes. */
  timeOptions = TIME_OPTIONS;

  groups = signal<Group[]>([]);

  /** Shared events the user has created, offered for linking to this post. */
  events = signal<ApiEvent[]>([]);

  /** Data injected into the dialog by the parent component. */
  data = inject<Post | null>(MAT_DIALOG_DATA);

  /** Reactive form holding post content, optional audience filters, and an optional linked event. */
  form = new FormGroup({
    content: new FormControl(''),
    careers: new FormControl<any[]>([]),
    interests: new FormControl<any[]>([]),
    grades: new FormControl<any[]>(GRADES),
    groups: new FormControl<any[]>([]),
    event_ids: new FormControl<number[]>([]),
    createEvent: new FormControl(false),
    event: new FormGroup({
      title: new FormControl(''),
      day: new FormControl<Date | null>(null),
      start_time: new FormControl(''),
      end_time: new FormControl(''),
      place: new FormControl(''),
      description: new FormControl(''),
    }),
  });
  /** Plain-text content binding (unused by submit; kept for template compatibility). */
  content = '';

  /** Quill toolbar config. Uses a custom image handler instead of Quill's default base64 embedding. */
  quillConfig = {
    toolbar: {
      container: [
        ['bold', 'italic', 'underline'],
        [{ header: [1, 2, 3, false] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link', 'image'],
        ['clean'],
      ],
      handlers: {
        image: this.imageHandler.bind(this),
      },
    },
  };

  /** Quill editor instance, set once the editor is initialized in the template. */
  quillEditor: any;

  ngOnInit(): void {
    this.loadGroups();
    this.loadEvents();
    if (this.data) {
      this.form.patchValue({
        careers: this.data.postCareers ?? [],
        interests: this.data.postInterests ?? [],
        grades: this.data.postGrades?.length
          ? GRADES.filter((g: string) => this.data!.postGrades.includes(parseInt(g)))
          : GRADES,
        groups: this.data.postGroups ?? [],
        event_ids: this.data.postEvents ?? [],
      });
      this.restoreDraftEvent(this.data.draftEvent);
    }
  }

  private restoreDraftEvent(saved: DraftEvent | null | undefined): void {
    if (!saved || !this.showCreateEventOption) return;
    const [y, m, d] = (saved.day ?? '').split('-').map(Number);
    this.form.patchValue({
      createEvent: true,
      event: {
        title: saved.title ?? '',
        day: saved.day ? new Date(y, m - 1, d) : null,
        start_time: saved.start_time ?? '',
        end_time: saved.end_time ?? '',
        place: saved.place ?? '',
        description: saved.description ?? '',
      },
    });
  }

  private draftEventDetails(): DraftEvent | null {
    if (!this.form.get('createEvent')!.value) return null;
    const e = this.form.get('event')!.value;
    return {
      title: e.title ?? '',
      day: e.day ? formatDate(e.day) : '',
      start_time: e.start_time ?? '',
      end_time: e.end_time ?? '',
      place: e.place ?? '',
      description: e.description ?? '',
    };
  }

  // Patch the Quill content once editor is ready
  onEditorCreated(editor: any): void {
    this.quillEditor = editor;
    if (this.data?.content) {
      this.quillEditor.root.innerHTML = this.data.content;
    }
  }

  loadGroups() {
    this.groupService.getManagedGroups().subscribe({
      next: (groups) => {
        this.groups.set(groups);
      },
      error: (err) => {
        console.error(err);
      },
    });
  }

  loadEvents(): void {
    this.eventService.getEvents().subscribe({
      next: (events) => this.events.set(events.filter((e) => e.is_shared)),
      error: (err) => console.error('Failed to load events:', err),
    });
  }

  /**
   * Label for an event in the link picker, e.g. "Career Fair · Oct 23, 2026, 1:45 PM".
   * @param {ApiEvent} event
   * @returns {string}
   */
  eventLabel(event: ApiEvent): string {
    return `${event.title} · ${eventWhenLabel(event.day, event.start_time)}`;
  }

  filterSwitch(): void {
    this.filter = !this.filter;
  }

  filterTimeOptions(value: string | null | undefined): string[] {
    const query = (value ?? '').trim().toLowerCase();
    if (!query) return this.timeOptions;
    return this.timeOptions.filter((t) =>
      t.toLowerCase().replace(/\s/g, '').includes(query.replace(/\s/g, '')),
    );
  }

  imageHandler(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.click();

    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;

      const formData = new FormData();
      formData.append('image', file);
      this.postService.sendImg(formData).subscribe({
        next: (res: any) => {
          const editor = this.quillEditor;
          editor.focus();

          const range = editor.getSelection(true) || {
            index: editor.getLength(),
          };

          editor.insertEmbed(range.index, 'image', res.url);
          editor.setSelection(range.index + 1, 0);
        },
      });
    };
  }

  private validateEventFields(): string | null {
    const e = this.form.get('event')!.value;
    const title = (e.title ?? '').trim();

    if (!title) return 'Event title is required.';
    if (!e.day) return 'Event date is required.';
    if (!e.start_time || !displayToTime(e.start_time)) {
      return 'Event start time is required, e.g. "10:30 AM".';
    }
    if (e.end_time && !displayToTime(e.end_time)) {
      return 'Event end time format not recognized, e.g. "11:00 AM".';
    }
    if (!e.place) return 'Event location is required.';
    if (!e.description) return 'Event description is required.';
    return null;
  }

  /**
   * Whether the "Save Draft" action should be offered: always for a new post,
   * and for an existing one only while it's still a draft. An already-published
   * post only ever gets the single update ("Post") action — there's no
   * unpublish flow.
   * @returns {boolean}
   */
  get showDraftOption(): boolean {
    return !this.data || this.data.status === 'draft';
  }

  /**
   * Whether the "Also create an event" toggle should be offered: only for a new
   * post or a draft. An already-published post either created its event when it
   * was published or didn't — its events are managed through "Linked Events"
   * instead, so editing it never creates another one.
   * @returns {boolean}
   */
  get showCreateEventOption(): boolean {
    return !this.data || this.data.status === 'draft';
  }

  /**
   * Saves the post with the given target status. A draft never creates its
   * linked event (nobody's seen the post yet), regardless of the "Also create
   * an event" toggle — it saves the event details instead, and the event is
   * only created on an actual publish.
   * Publishing with linked events the audience can't see yet asks for
   * confirmation first, since linking shares them with that audience.
   * @param {'draft' | 'edited'} status
   * @returns {void}
   */
  save(status: 'draft' | 'edited'): void {
    const wantsEvent =
      this.showCreateEventOption && status !== 'draft' && !!this.form.get('createEvent')!.value;

    if (wantsEvent) {
      const validationError = this.validateEventFields();
      if (validationError) {
        this.snackBar.open(validationError, 'Dismiss', { duration: 5000 });
        return;
      }
    }

    const newlyShared = status === 'draft' ? [] : this.newlySharedEvents();
    if (newlyShared.length === 0) {
      this.submit(wantsEvent, status);
      return;
    }

    const names = newlyShared.map((e) => e.title).join(', ');
    const data = {
      title: 'Share linked events with this audience',
      content: `Linking ${newlyShared.length} event${newlyShared.length !== 1 ? 's' : ''} (${names}) will share ${newlyShared.length !== 1 ? 'them' : 'it'} with everyone who can see this post. Are you sure you want to continue?`,
      confirmLabel: 'Share',
      icon: 'event',
    };

    const dialogRef = this.dialog.open(ConfirmCancelDialog, { data });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.submit(wantsEvent, status);
      }
    });
  }

  /**
   * Linked events the post's audience can't see yet: every selected event when
   * the post is new or still a draft, otherwise only ones added since it was
   * published.
   * @returns {ApiEvent[]}
   */
  private newlySharedEvents(): ApiEvent[] {
    const selected = this.form.get('event_ids')!.value ?? [];
    const alreadyShared =
      this.data && this.data.status !== 'draft' ? (this.data.postEvents ?? []) : [];
    return this.events().filter((e) => selected.includes(e.id) && !alreadyShared.includes(e.id));
  }

  /**
   * Saves the post, then creates its linked event when one was requested.
   * @param {boolean} wantsEvent
   * @param {'draft' | 'edited'} status
   * @returns {void}
   */
  private submit(wantsEvent: boolean, status: 'draft' | 'edited'): void {
    const { createEvent, event, ...rest } = this.form.value;
    const payload = {
      ...rest,
      content: this.quillEditor.root.innerHTML,
      status,
      ...(status === 'draft' && { draft_event: this.draftEventDetails() }),
    };
    const request$ = this.data
      ? this.postService.updatePost(this.data.id, payload)
      : this.postService.createPost(payload);

    request$.subscribe({
      next: (post: any) => {
        if (wantsEvent) {
          this.createLinkedEvent(post);
        } else {
          this.dialogRef.close(post);
        }
      },
      error: (err) => {
        const message = err?.error?.error || 'Failed to save post. Please try again.';
        this.snackBar.open(message, 'Dismiss', { duration: 5000 });
      },
    });
  }

  private createLinkedEvent(post: any): void {
    const v = this.form.value;
    const e = v.event!;
    const title = (e.title ?? '').trim();
    const start_time = displayToTime(e.start_time!);
    const end_time = e.end_time ? displayToTime(e.end_time) || undefined : undefined;

    const eventPayload = {
      title: title,
      day: formatDate(e.day as Date),
      start_time,
      end_time,
      place: e.place || undefined,
      description: e.description || undefined,
      is_shared: true,
      grades: (v.grades ?? []).map((g: string) => parseInt(g, 10)),
      groups: v.groups ?? [],
      post_ids: [post.id],
    };

    this.eventService.createEvent(eventPayload).subscribe({
      next: () => this.dialogRef.close(post),
      error: (err) => {
        console.error('Linked event creation failed:', err);
        this.snackBar.open(
          'Post was created, but the linked event could not be saved.',
          'Dismiss',
          { duration: 5000 },
        );
        this.dialogRef.close(post);
      },
    });
  }
}
